import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Header from '../../main/components/Header';
import LeftSections from '../components/LeftSection';
import CategoryHeader from '../components/CategoryHeader';
import SearchBar from '../components/SearchBar';
import Result from '../components/Result';
import Pagination from '../components/Pagination';
import Path from '../../path/ui/Path';
import { getFilterAnnouncements } from '../../../api/getFilterAnnouncements';
import '../style/Main.css';
import SearchStart from '../../modals/search_modal/components/SearchStart';
import MobileBottomBar from '../../main/components/MobileBottomBar';
import CategoryModal from '../../modals/category_modal/ui/Main';
import FilterModal from '../../modals/filter_modal/ui/Main';
import Icon from '../../../common/components/Icon';
import { t } from 'i18next';
import monitoringService from '../../../services/monitoringService';

function Main() {
    const { category_name, subcategory_name, category_id, subcategory_id } = useParams();
    const location = useLocation();

    // States for filters and items
    const [searchQuery, setSearchQuery] = useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('q') || '';
    });
    const [sortOption, setSortOption] = useState('');
    const getInitialFilters = () => {
        try {
            const stored = localStorage.getItem('selectedLocation');
            const loc = stored ? JSON.parse(stored) : null;
            return {
                minPrice: '',
                maxPrice: '',
                negotiable: false,
                country: loc?.country || '',
                province: loc?.province || '',
                locality: loc?.locality || '',
                area: loc?.area || '',
                latitude: loc?.latitude || '',
                longitude: loc?.longitude || ''
            };
        } catch {
            return {
                minPrice: '',
                maxPrice: '',
                negotiable: false,
                country: '',
                province: '',
                locality: '',
                area: '',
                latitude: '',
                longitude: ''
            };
        }
    };

    const [filters, setFilters] = useState(getInitialFilters);
    const [filteredItems, setFilteredItems] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [categoryId, setCategoryId] = useState();
    const [subCategoryId, setSubCategoryId] = useState();
    const itemsPerPage = 12;

    // Modals and Mobile
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Update `isMobile` on resize
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [onSelect, setOnSelect] = useState(
        category_name
            ? subcategory_name && subcategory_name !== 'null'
                ? `${category_name}, ${subcategory_name}`
                : category_name
            : null
    );

    // Sync selected category label when URL params change
    useEffect(() => {
        if (category_name) {
            setOnSelect(
                subcategory_name && subcategory_name !== 'null'
                    ? `${category_name}, ${subcategory_name}`
                    : category_name
            );
        } else {
            setOnSelect(null);
        }
        setCategoryId(category_id);
        setSubCategoryId(subcategory_id);
    }, [category_name, subcategory_name, category_id, subcategory_id]);

    const fetchItems = useCallback(async () => {
        const appliedFilters = {
            ...filters,
            q: searchQuery,
            page: currentPage,
            limit: itemsPerPage,
            negotiable: filters.negotiable,
        };
        // Category listing: do not filter by header location so all ads in category are shown
        delete appliedFilters.country;
        delete appliedFilters.province;
        delete appliedFilters.locality;
        delete appliedFilters.area;
        delete appliedFilters.latitude;
        delete appliedFilters.longitude;

        // Only add category_id and subcategory_id if they are valid numbers
        const finalCategoryId = categoryId || category_id;
        const finalSubcategoryId = subCategoryId || subcategory_id;
        
        if (finalCategoryId && finalCategoryId !== 'null' && finalCategoryId !== null && !isNaN(Number(finalCategoryId))) {
            appliedFilters.category_id = finalCategoryId;
        }
        
        if (finalSubcategoryId && finalSubcategoryId !== 'null' && finalSubcategoryId !== null && !isNaN(Number(finalSubcategoryId))) {
            appliedFilters.subcategory_id = finalSubcategoryId;
        }
        
        // Handle sort options
        if (sortOption === 'date_newest') {
            appliedFilters.sort_by_date = 'desc';
        } else if (sortOption === 'date_oldest') {
            appliedFilters.sort_by_date = 'asc';
        } else if (sortOption === 'price_low') {
            appliedFilters.sort_by_price = 'asc';
        } else if (sortOption === 'price_high') {
            appliedFilters.sort_by_price = 'desc';
        }
        
        // Only include price filters if they are explicitly set (not empty string)
        if (filters.minPrice !== '' && filters.minPrice !== null && filters.minPrice !== undefined) {
            appliedFilters.min_price = filters.minPrice;
        }
        if (filters.maxPrice !== '' && filters.maxPrice !== null && filters.maxPrice !== undefined) {
            appliedFilters.max_price = filters.maxPrice;
        }

        try {
            const { items, total } = await getFilterAnnouncements(appliedFilters);
            setFilteredItems(items);
            setTotalItems(total);
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    }, [filters, searchQuery, sortOption, categoryId, subCategoryId, category_id, subcategory_id, currentPage]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    // Update search query when URL changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const queryFromURL = params.get('q') || '';
        if (queryFromURL && queryFromURL !== searchQuery) {
            setSearchQuery(queryFromURL);
            setCurrentPage(1);
        }
    }, [location.search, searchQuery]);

    // Track category view when component mounts or category changes
    useEffect(() => {
        const categoryIdToTrack = categoryId || category_id;
        if (categoryIdToTrack) {
            monitoringService.trackCategoryView(categoryIdToTrack, category_name).catch(err => {
                console.warn('Failed to track category view:', err);
            });
        }
    }, [categoryId, category_id, category_name]);

    // Track search when search query changes and is not empty
    useEffect(() => {
        if (searchQuery && searchQuery.trim()) {
            const categoryIdToTrack = categoryId || category_id;
            monitoringService.trackSearch(searchQuery, categoryIdToTrack).catch(err => {
                console.warn('Failed to track search:', err);
            });
        }
    }, [searchQuery, categoryId, category_id]);

    // Handlers
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    }, []);

    const handlePriceChange = (min, max) => {
        setFilters((prev) => ({ ...prev, minPrice: min, maxPrice: max }));
    };

    const handleToggleChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setFilters({
            minPrice: '',
            maxPrice: '',
            negotiable: false,
            country: '',
            province: '',
            locality: '',
            area: '',
            latitude: '',
            longitude: ''
        });
        setSearchQuery('');
        setSortOption('');
        setOnSelect(null);
        setCurrentPage(1);
    };

    // Sync location filters with Header location selector (localStorage)
    useEffect(() => {
        const syncFromStorage = () => {
            try {
                const stored = localStorage.getItem('selectedLocation');
                const loc = stored ? JSON.parse(stored) : null;
                if (!loc) return;
                setFilters((prev) => ({
                    ...prev,
                    country: loc.country || '',
                    province: loc.province || '',
                    locality: loc.locality || '',
                    area: loc.area || '',
                    latitude: loc.latitude || '',
                    longitude: loc.longitude || ''
                }));
                setCurrentPage(1);
            } catch {
                // ignore
            }
        };

        // Initial sync (in case Header was changed while staying on page)
        syncFromStorage();

        const onStorage = (e) => {
            if (e.key === 'selectedLocation') {
                syncFromStorage();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    return (
        <div className="def_background">
            <Header />
            <Path currentPath={`${category_name || ''}/${subcategory_name || ''}`} />
            {isMobile && <CategoryHeader header_text={category_name} />}
            <div className="main_category_content px-5 md:px-5 lg:px-[60px] xl:px-[120px] 2xl:px-[240px]">
                <LeftSections
                    openCategoryModal={() => setIsCategoryModalOpen(true)}
                    selectedCategory={onSelect}
                    resetFilters={resetFilters}
                    minPrice={filters.minPrice}
                    maxPrice={filters.maxPrice}
                    onPriceChange={handlePriceChange}
                    isContract={filters.negotiable}
                    onContractToggle={(value) => handleToggleChange('negotiable', value)}
                />
                <button className="open_modal_button open_modal_button_filter" onClick={() => setIsFilterModalOpen(true)}>
                    {t('filters')}<Icon src="filter" alt="filter" />
                </button>
                {isMobile && (
                    <SearchBar
                        onSearch={handleSearch}
                        onSort={(value) => { setSortOption(value); setCurrentPage(1); }}
                        fullWidthDropdown
                    />
                )}
                <div className="main_category_right_section">
                    {!isMobile && <CategoryHeader header_text={category_name} />}
                    {!isMobile && <SearchBar onSearch={handleSearch} onSort={(value) => { setSortOption(value); setCurrentPage(1); }} />}
                    {filteredItems.length > 0 ? (
                        <Result items={filteredItems} />
                    ) : (
                        <SearchStart />
                    )}
                    {totalItems > itemsPerPage && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(totalItems / itemsPerPage)}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            </div>
            <MobileBottomBar />
            {isCategoryModalOpen && (
                <CategoryModal
                    onSelect={(selected) => {
                        setOnSelect(`${selected.category.name}, ${selected.subcategory.name}`);
                        setCategoryId(selected.category.id);
                        setSubCategoryId(selected.subcategory.id);
                    }}
                    onClose={() => setIsCategoryModalOpen(false)}
                />
            )}
            {isFilterModalOpen && (
                <FilterModal
                    openCategoryModal={() => setIsCategoryModalOpen(true)}
                    openLocationModal={() => {}}
                    selectedCategory={onSelect}
                    onClose={() => setIsFilterModalOpen(false)}
                    onApplyFilters={(appliedFilters) => {
                        setFilters(appliedFilters);
                        setIsFilterModalOpen(false);
                        setCurrentPage(1);
                    }}
                    resetFilters={resetFilters}
                    minPrice={filters.minPrice}
                    maxPrice={filters.maxPrice}
                    isContract={filters.negotiable}
                />
            )}
        </div>
    );
}

export default Main;